import { supabase } from '../lib/supabase';

interface SalesforceConfig {
  clientId: string;
  clientSecret: string;
  instanceUrl: string;
  accessToken?: string;
  refreshToken?: string;
}

interface SalesforceAuthResponse {
  access_token: string;
  refresh_token: string;
  instance_url: string;
  id: string;
  token_type: string;
}

interface SalesforceContact {
  Id?: string;
  Email?: string;
  Phone?: string;
  Description?: string;
  LeadSource?: string;
  Status?: string;
  CreatedDate?: string;
  LastModifiedDate?: string;
  IsDeleted?: boolean;
}

interface LeadData {
  type: string;
  value: string;
  conversation_id: string;
  chatbot_public_id: string;
  metadata?: any;
  created_at?: string;
  conversation_messages?: Array<{
    content: string;
    role: string;
    created_at: string;
  }>;
}

export class SalesforceService {
  private config: SalesforceConfig;
  private chatbotPublicId: string;

  constructor(config: SalesforceConfig, chatbotPublicId: string) {
    this.config = config;
    this.chatbotPublicId = chatbotPublicId;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Ensure we have a valid access token
    await this.ensureValidToken();
    
    const url = `${this.config.instanceUrl}/services/data/v56.0${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.config.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, refresh and retry
        await this.refreshToken();
        return this.makeRequest(endpoint, options);
      }
      throw new Error(`Salesforce API error: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  private async ensureValidToken(): Promise<void> {
    if (!this.config.accessToken) {
      await this.getToken();
    }
  }
  
  private async getToken(): Promise<void> {
    const tokenUrl = `${this.config.instanceUrl}/services/oauth2/token`;
    const params = new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: this.config.clientId,
      client_secret: this.config.clientSecret
    });
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get Salesforce token: ${response.statusText}`);
    }
    
    const data: SalesforceAuthResponse = await response.json();
    this.config.accessToken = data.access_token;
    this.config.refreshToken = data.refresh_token;
  }
  
  private async refreshToken(): Promise<void> {
    if (!this.config.refreshToken) {
      await this.getToken();
      return;
    }
    
    const tokenUrl = `${this.config.instanceUrl}/services/oauth2/token`;
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      client_secret: this.config.clientSecret,
      refresh_token: this.config.refreshToken
    });
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    });
    
    if (!response.ok) {
      // If refresh fails, get a new token
      await this.getToken();
      return;
    }
    
    const data: SalesforceAuthResponse = await response.json();
    this.config.accessToken = data.access_token;
    this.config.refreshToken = data.refresh_token;
  }

  async syncLead(leadId: string): Promise<void> {
    try {
      console.log(`Syncing lead ${leadId} to Salesforce...`);
      
      // Récupérer les informations du lead depuis la base de données
      const { data: leadData, error } = await supabase
        .from('conversation_contact_info')
        .select(`
          *,
          conversation:conversation_id(
            messages:conversation_messages(
              content,
              role,
              created_at
            )
          )
        `)
        .eq('id', leadId)
        .single();
      
      if (error) throw error;
      if (!leadData) throw new Error(`Lead with ID ${leadId} not found`);
      
      // Préparer les données pour Salesforce
      const leadPayload: any = {
        LeadSource: 'Chatbot',
        Company: 'Not provided',
        Description: this.formatConversationHistory(leadData as any),
        Status: 'New'
      };
      
      // Ajouter des métadonnées personnalisées si disponibles
      let metadata = {};
      try {
        // Essayer de récupérer les métadonnées du lead si elles existent
        const { data: metadataResult, error: metadataError } = await supabase
          .from('conversation_contact_info')
          .select('metadata')
          .eq('id', leadId)
          .single();
          
        if (!metadataError && metadataResult && metadataResult.metadata) {
          metadata = metadataResult.metadata;
          
          // Ajouter des champs supplémentaires si disponibles
          if (metadata.company) {
            leadPayload.Company = metadata.company;
          }
          
          if (metadata.name) {
            const nameParts = metadata.name.split(' ');
            leadPayload.FirstName = nameParts[0] || '';
            leadPayload.LastName = nameParts.slice(1).join(' ') || 'Unknown';
          }
        }
      } catch (metaErr) {
        console.warn('Error retrieving metadata:', metaErr);
      }
      
      // Ajouter email ou téléphone en fonction du type
      if (leadData.type === 'email') {
        leadPayload.Email = leadData.value;
        
        // Si le nom n'est pas encore défini
        if (!leadPayload.LastName) {
          leadPayload.LastName = 'Unknown';
        }
      } else if (leadData.type === 'phone') {
        leadPayload.Phone = leadData.value;
        leadPayload.LastName = leadPayload.LastName || 'Unknown';
      }
      
      // Créer le lead dans Salesforce
      const response = await this.makeRequest<{ id: string; success: boolean }>(
        '/sobjects/Lead',
        {
          method: 'POST',
          body: JSON.stringify(leadPayload)
        }
      );
      
      if (!response.success) {
        throw new Error('Failed to create lead in Salesforce');
      }
      
      // Mettre à jour le lead dans la base de données avec l'ID Salesforce
      const { error: updateError } = await supabase
        .from('conversation_contact_info')
        .update({
          processed: true,
          external_id: response.id,
          metadata: {
            ...metadata,
            salesforce: {
              id: response.id,
              syncedAt: new Date().toISOString()
            }
          }
        })
        .eq('id', leadId);
      
      if (updateError) throw updateError;
      
      console.log(`Lead ${leadId} successfully synced to Salesforce with ID ${response.id}`);
    } catch (err) {
      console.error('Error syncing lead to Salesforce:', err);
      throw err;
    }
  }
  
  /**
   * Formate l'historique de conversation pour l'inclure dans la description du lead
   */
  private formatConversationHistory(leadData: LeadData): string {
    if (!leadData.conversation_messages || leadData.conversation_messages.length === 0) {
      return `Contact information: ${leadData.value}`;
    }
    
    const messages = leadData.conversation_messages
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map(msg => {
        const role = msg.role === 'user' ? 'Customer' : 'Chatbot';
        return `[${role}]: ${msg.content}`;
      })
      .join('\n\n');
    
    return `Contact: ${leadData.value}\n\nConversation History:\n${messages}`;
  }

  async syncContactsWebhook(webhookData: any): Promise<void> {
    try {
      const eventType = webhookData.event.type;
      const sobjectId = webhookData.data.sobject.Id;

      switch (eventType) {
        case 'created':
        case 'updated': {
          // Récupérer les détails du contact/lead
          const contact = await this.makeRequest<SalesforceContact>(
            `/sobjects/Lead/${sobjectId}`
          );
          await this.updateLocalContact(contact);
          break;
        }
        case 'deleted': {
          await this.deleteLocalContact(sobjectId);
          break;
        }
        case 'undeleted': {
          const contact = await this.makeRequest<SalesforceContact>(
            `/sobjects/Lead/${sobjectId}`
          );
          await this.updateLocalContact(contact);
          break;
        }
      }
    } catch (err) {
      console.error('Error processing Salesforce webhook:', err);
      throw err;
    }
  }

  private async updateLocalContact(contact: SalesforceContact): Promise<void> {
    try {
      const { error } = await supabase
        .from('conversation_contact_info')
        .upsert({
          // external_id: contact.Id,
          chatbot_public_id: this.chatbotPublicId,
          type: contact.Email ? 'email' : 'phone',
          value: contact.Email || contact.Phone || '',
          processed: true,
          metadata: {
            salesforce: {
              id: contact.Id,
              status: contact.Status,
              leadSource: contact.LeadSource,
              lastModifiedDate: contact.LastModifiedDate
            }
          }
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error updating local contact:', err);
      throw err;
    }
  }

  private async deleteLocalContact(contactId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('conversation_contact_info')
        .delete()
        .eq('external_id', contactId)
        .eq('chatbot_public_id', this.chatbotPublicId);

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting local contact:', err);
      throw err;
    }
  }
}